// src/hooks/useBranding.ts
// Fetches branding settings from /api/branding and injects CSS custom properties
// into :root so the entire app responds to hospital color/font choices.

import { useState, useEffect } from 'react';
import { pickReadableAccent, ensureDark } from '../lib/color';
import { VOICE_LANG_KEY } from '../lib/voiceLanguages';

/** The full branding_settings shape, as returned by GET /api/branding and
 * edited from Admin Panel → Branding. Every *_url field arrives from the
 * server already rewritten to this tenant's own asset-proxy path (see
 * fetchBranding below) — never a raw storage-bucket URL. */
export interface BrandingSettings {
  hospital_name: string;
  tagline: string;
  primary_color: string;
  accent_color: string;
  navy_color: string;
  nav_text_color: string;
  font_choice: string;
  footer_text: string;
  logo_url: string | null;
  favicon_url: string | null;
  login_bg_url: string | null;
  default_voice_language: string;
  /** Present on the raw GET response; used only to cache-bust asset URLs. */
  updated_at?: string;
}

/** Bundled ClintoBytes artwork (frontend/public) — what the UI shows when a
 * hospital has not uploaded its own logo. */
export const DEFAULT_LOGO_URL = '/clintobytes-logo.png';
export const DEFAULT_ICON_URL = '/clintobytes-icon.png';

const DEFAULTS: BrandingSettings = {
  hospital_name: 'ClintoBytes Health',
  tagline:       'Hospital Management System',
  primary_color: '0B5FD6',
  accent_color:  '00D9A5',
  navy_color:    '0B1D33',
  nav_text_color: 'FFFFFF',
  font_choice:   'DM Sans',
  footer_text:   'Confidential — Internal Use Only',
  logo_url:      null,
  favicon_url:   null,
  login_bg_url:  null,
  default_voice_language: 'en-IN',
};

export const GOOGLE_FONTS = [
  'DM Sans', 'Inter', 'Roboto', 'Poppins', 'Lato', 'Nunito', 'Open Sans',
  'Source Sans Pro', 'Raleway', 'Merriweather',
];

// Fonts installed on the computer (Windows / Microsoft Office), not served by
// Google Fonts — so they are never requested from fonts.googleapis.com. Each
// carries its own fallback: a metric-compatible substitute where one exists,
// then the right generic family (Times New Roman is a serif).
export const SYSTEM_FONTS: Record<string, string> = {
  'Times New Roman': "'Times New Roman', Tinos, 'Liberation Serif', Times, serif",
  'Calibri':         "Calibri, Carlito, 'Segoe UI', system-ui, sans-serif",
};

/** The CSS font-family stack for a Branding font choice. */
export function fontStack(font: string | null | undefined): string {
  const name = font || 'DM Sans';
  return SYSTEM_FONTS[name] ?? `'${name}', 'Segoe UI', system-ui, sans-serif`;
}

// "#RRGGBB" -> "R,G,B", so callers that need an alpha-blended tint (e.g. a
// translucent highlight background) can use rgba(var(--accent-rgb), .12)
// instead of a color that can't carry opacity.
function hexToRgbTriplet(hex: string | null | undefined): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
  if (!m) return '0,217,165';
  return [m[1]!, m[2]!, m[3]!].map(h => parseInt(h, 16)).join(',');
}

function injectCSS(branding: BrandingSettings): void {
  const r = document.documentElement;
  r.style.setProperty('--teal',   `#${branding.primary_color}`);
  r.style.setProperty('--accent', `#${branding.accent_color}`);
  r.style.setProperty('--accent-rgb', hexToRgbTriplet(branding.accent_color));
  r.style.setProperty('--navy',   `#${branding.navy_color}`);
  r.style.setProperty('--nav-text', `#${branding.nav_text_color}`);
  r.style.setProperty('--nav-text-rgb', hexToRgbTriplet(branding.nav_text_color));
  // Accent-colored TEXT sitting directly on the navy bar (App.tsx's sidebar
  // tagline/role labels, VisitStepper's Visit ID and progress percentage) —
  // as opposed to --accent used decoratively (a border, a translucent nav
  // highlight, a gradient), which stays readable regardless of hue since
  // it's never the only thing carrying the text. A hospital's chosen accent
  // color has no guaranteed contrast against their chosen navy — nothing
  // stops both being picked as similarly mid-toned, saturated colors — so
  // this falls back to nav_text_color (itself already contrast-checked
  // against navy in the Branding tab) rather than rendering illegible text.
  r.style.setProperty('--accent-on-navy', `#${pickReadableAccent(branding.navy_color, branding.accent_color, branding.nav_text_color)}`);
  // A guaranteed-dark variant of navy_color, same hue — for fixed chrome
  // (VisitStepper's compact bar) that needs to read as a dark surface even
  // when a hospital's own navy_color isn't dark at all (nothing forces it
  // to be; a bright teal or pastel is a valid Branding-tab choice). Using
  // --navy verbatim there can end up looking like a bug on a light-navy
  // tenant; falling back to a fixed neutral throws their color identity
  // away. This keeps the hue, just deepens it. See ensureDark's own comment.
  r.style.setProperty('--navy-dark', `#${ensureDark(branding.navy_color)}`);
  r.style.setProperty('--font',   fontStack(branding.font_choice));

  // Load the Google Font dynamically — not for DM Sans (index.html already
  // loads it) and not for a system font (Google doesn't serve those).
  const linkId = 'dynamic-font';
  if (branding.font_choice && branding.font_choice !== 'DM Sans' && !(branding.font_choice in SYSTEM_FONTS)) {
    const fontName = encodeURIComponent(branding.font_choice);
    let link = document.getElementById(linkId) as HTMLLinkElement | null;
    if (!link) { link = document.createElement('link'); link.id = linkId; link.rel = 'stylesheet'; document.head.appendChild(link); }
    link.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;500;600&display=swap`;
  } else {
    document.getElementById(linkId)?.remove();
  }

  // Favicon
  if (branding.favicon_url) {
    let fav = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (!fav) { fav = document.createElement('link'); fav.rel = 'icon'; document.head.appendChild(fav); }
    fav.href = branding.favicon_url;
  }

  // Page title
  if (branding.hospital_name) document.title = branding.hospital_name;
}

export interface UseBrandingResult {
  branding: BrandingSettings;
  loading: boolean;
  refresh: () => void;
  GOOGLE_FONTS: string[];
}

export function useBranding(): UseBrandingResult {
  const [branding, setBranding] = useState<BrandingSettings>(DEFAULTS);
  const [loading, setLoading]   = useState(true);

  const fetchBranding = async () => {
    try {
      const res  = await fetch('/api/branding');
      const data: Partial<BrandingSettings> = await res.json();
      const merged: BrandingSettings = { ...DEFAULTS, ...data };
      // The stored *_url fields point at a private storage bucket object (not
      // publicly readable) — route through our own backend proxy instead,
      // which streams the bytes with real credentials. Cache-busted with
      // updated_at so a re-uploaded logo doesn't keep showing a stale cached copy.
      const v = data.updated_at ? `?v=${encodeURIComponent(data.updated_at)}` : '';
      if (merged.logo_url)     merged.logo_url     = `/api/branding/asset/logo${v}`;
      if (merged.favicon_url)  merged.favicon_url  = `/api/branding/asset/favicon${v}`;
      if (merged.login_bg_url) merged.login_bg_url = `/api/branding/asset/login_bg${v}`;
      setBranding(merged);
      injectCSS(merged);
      // Every voice-enabled control across the app reads its default
      // language from this one localStorage key (see lib/voiceLanguages.ts)
      // — syncing the tenant's admin-configured value here on every load is
      // what makes it "common everywhere" without wiring a React context
      // through every mic button.
      if (merged.default_voice_language) localStorage.setItem(VOICE_LANG_KEY, merged.default_voice_language);
    } catch {
      injectCSS(DEFAULTS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBranding(); }, []);

  const refresh = () => { fetchBranding(); };

  return { branding, loading, refresh, GOOGLE_FONTS };
}

export default useBranding;
